import InputField from '@allesauto/core/inputField';
import Textarea from '@allesauto/core/textarea';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import clsx from 'clsx';
import { useFormik } from 'formik';
import React, { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import useTranslationContactDealer from '@/components/forms/contactDealerForm/contactDealerTranslation';
import OverlaySuccess from '@/components/forms/overlaySuccess/OverlaySuccess';
import PrivacyNotice from '@/components/forms/PrivacyNotice/PrivacyNotice';
import ReCaptchaValidator, {
  ITokenValidator,
} from '@/components/ReCaptcha/ReCaptchaValidator';

import {
  sharedOverlayDetailsSteps,
  sharedOverlaySuccessTranslations,
} from '@/global/funcs/overlaySuccessSharedTranslations';
import { useC2cContact } from '@/global/hooks/useC2cVehicle';
import useNewsletterSubscribeCheckbox from '@/global/hooks/useNewsletterSubscribeCheckbox';
import {
  closeOverlay,
  openOverlay,
  setOverlayLoadingState,
} from '@/store/redux/actions/app';

import Footer from '../../OverlayFormFooter/OverlayFormFooter';

interface IC2COverlayComponent {
  c2cConcreteId: string;
  make: string;
  model: string;
  dealerName: string;
  contactPerson: SchemaTypes.ContactPerson | undefined;
  darkBg?: boolean;
}

const C2COverlayComponent: React.FC<
  React.PropsWithChildren<IC2COverlayComponent>
> = ({
  c2cConcreteId,
  make,
  model,
  dealerName,
  contactPerson,
  darkBg = false,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { contactC2c } = useC2cContact();

  const overlayLoadingState = useSelector(
    (state: any) => state.app.overlayProps.isLoading
  );

  const successOverlayDetailsSteps: React.ReactNode[] = useMemo(() => {
    return contactPerson
      ? sharedOverlayDetailsSteps(intl, dealerName, contactPerson)
      : [];
  }, [intl, dealerName, contactPerson]);

  const userMessagePrefilled = translate(
    intl,
    'ui.FORMS.CONTACT_DEALER.USER_MSG_PREFILLED',
    {
      car: `${make} ${model}`,
    }
  );

  const memoizedOverlaySuccessTranslations = useMemo(() => {
    return sharedOverlaySuccessTranslations(intl, dealerName);
  }, [intl, dealerName]);

  const tokenValidator = useRef<ITokenValidator | undefined>();

  const setTokenValidator = useCallback((v: ITokenValidator) => {
    tokenValidator.current = v;
  }, []);

  const {
    NewsletterSubscribeCheckbox,
    checkboxProps,
    subscribe: subscribeToNewsletter,
  } = useNewsletterSubscribeCheckbox({ isDarkBg: false });

  const {
    memoizedContactC2cFormSchema,
    memoizedContactDealerFormTranslations,
    memoizedContactC2CFormTranslations,
  } = useTranslationContactDealer();

  const formik = useFormik({
    initialValues: {
      userMsg: userMessagePrefilled,
      firstName: '',
      lastName: '',
      email: '',
    },

    onSubmit: async (data) => {
      if (process.env.NEXT_PUBLIC_FEATUREFLAG_RECAPTCHA) {
        const token = await tokenValidator.current?.validate();
        if (!token) {
          return false;
        }
      }

      dispatch(setOverlayLoadingState(true));

      const formData: SchemaTypes.C2cContactInput = {
        message: data.userMsg || userMessagePrefilled,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
      };

      try {
        const result = await contactC2c(c2cConcreteId, formData);

        if (!result) {
          throw new Error('Could not submit');
        }

        subscribeToNewsletter({
          email: formData.email,
          source: 'SubscribeContactDealerCommon',
        });

        dispatch(closeOverlay());

        setTimeout(
          () =>
            dispatch(
              openOverlay({
                children: (
                  // TODO: add headline, description and favorite button when ready
                  // bodyHeadline="My Headline"
                  // bodyDescription="My description"
                  // textLink={<TextLink onClick={onFavListClick} />}
                  <OverlaySuccess
                    bannerHeadline={
                      memoizedOverlaySuccessTranslations.SUBMISSION_THANK_YOU
                    }
                    bannerDescription={
                      memoizedOverlaySuccessTranslations.DEALER_CONFIRMATION_MSG
                    }
                    detailsHeadline={
                      memoizedOverlaySuccessTranslations.NEXT_STEPS_TEXT
                    }
                    details={successOverlayDetailsSteps}
                    ctaLabel={memoizedOverlaySuccessTranslations.BUTTON_TEXT}
                    onBtnClick={() => dispatch(closeOverlay())}
                  />
                ),
                title: '',
                withPaddings: false,
              })
            ),
          100
        );
      } catch (e) {
        // the error is handled by the apollo errorHandler
        dispatch(setOverlayLoadingState(false));
      }
    },

    validationSchema: memoizedContactC2cFormSchema,
  });

  const className = {
    error: 'text-red60 text-p3 mt-10px',
    newsletterPrivacy: 'mt-60px',
    privacyNotice: clsx(
      'font-regular mt-20px text-p3',
      darkBg ? 'text-white' : 'text-grey60'
    ),
    requiredFieldNote: clsx(
      'font-regular mt-20px pb-10px text-p3',
      darkBg ? 'text-white' : 'text-grey80'
    ),
    extraText: 'font-regular text-p3 text-grey80 mt-20px',
  };

  return (
    <form className="mx-[3px] pb-100px">
      <div className="h-full mb-20px">
        <Textarea
          label={memoizedContactDealerFormTranslations.USER_MSG_LABEL}
          className="mt-20px"
          name="userMsg"
          value={formik.values.userMsg}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          darkBg={darkBg}
        />
        {formik.errors.userMsg ? (
          <div className={className.error}>{formik.errors.userMsg}</div>
        ) : (
          ''
        )}

        <InputField
          placeholder={`${memoizedContactDealerFormTranslations.FIRST_NAME}*`}
          errorMessage={formik.touched.firstName ? formik.errors.firstName : ''}
          name="firstName"
          customStyle="mt-30px"
          value={formik.values.firstName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          darkBg={darkBg}
        />
        <InputField
          placeholder={`${memoizedContactDealerFormTranslations.LAST_NAME}*`}
          errorMessage={formik.touched.lastName ? formik.errors.lastName : ''}
          customStyle="mt-30px"
          name="lastName"
          value={formik.values.lastName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          darkBg={darkBg}
        />

        <InputField
          type="email"
          errorMessage={formik.touched.email ? formik.errors.email : ''}
          placeholder={`${memoizedContactDealerFormTranslations.EMAIL_LABEL}*`}
          customStyle="mt-30px text-purple80"
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          darkBg={darkBg}
        />

        <div className={className.newsletterPrivacy}>
          <NewsletterSubscribeCheckbox {...checkboxProps} />
          <PrivacyNotice
            translationKey="ui.FORMS.COMMON.PRIVACY_NOTICE.CONTACT_DEALER"
            className={className.privacyNotice}
          />
        </div>

        <div className={className.extraText}>
          {memoizedContactC2CFormTranslations.MESSAGE}
        </div>

        <div className={className.requiredFieldNote}>
          {memoizedContactDealerFormTranslations.REQUIRED_FIELD_NOTE}
        </div>
      </div>

      {process.env.NEXT_PUBLIC_FEATUREFLAG_RECAPTCHA && (
        <ReCaptchaValidator setStepValidator={setTokenValidator} />
      )}

      <Footer
        buttonText={memoizedContactDealerFormTranslations.BUTTON_TEXT}
        onButtonClick={formik.handleSubmit}
        isButtonDisabled={formik.isSubmitting}
        className="!z-50"
        showButtonLoader={overlayLoadingState || false}
      />
    </form>
  );
};

export default C2COverlayComponent;
